import React, { useCallback, useEffect, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Shared } from 'ui/shared'
import { Header } from './components/Header/Header'
import { Grid } from '@mui/material'
import { DatePicker } from 'ui/shared/components/DatePicker/DatePicker'
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import {
  googleMapsComponentOptions,
  googleMapsDefaultCenter,
  googleMapsJsApiLoaderParams,
  googleMapsZoom,
} from 'utils/constants/googleMaps'
import { useDispatch } from 'react-redux'
import { Actions_Bookings } from 'redux/modules/bookings/Actions'
import { TVisibleRegion } from 'utils/types/TVisibleRegion'
import { Markers } from './components/Markers/Markers'
import { useSelector } from 'react-redux'
import { TState } from 'redux/config/root-types'
import { TrailerList } from './components/TrailerList/TrailerList'
import { LocationInput } from './components/SearchInput/LocationInput'
import { TLocation } from 'utils/types/TLocation'
import { Dropdown } from 'ui/shared/components/Dropdown/Dropdown'
import { Actions_TrailerTypes } from 'redux/modules/trailerTypes/Actions'
import { ITrailerFull } from 'redux/modules/trailers/TReducer'
import { CreateBookingModal } from './components/CreateBookingModal/CreateBookingModal'

type TFilterTime = (time: Date) => boolean

export const CreateBooking = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { isLoaded } = useJsApiLoader(googleMapsJsApiLoaderParams)

  const selectedPickUpParking = useSelector((state: TState) => state.bookings.selectedPickUpParking)
  const selectedTrailerTypeId = useSelector((state: TState) => state.bookings.selectedTrailerTypeId)
  const trailerTypes = useSelector((state: TState) => state.trailerTypes.data)

  const [pickUpLocation, setPickUpLocation] = useState<TLocation | null>(null)
  const [pickUpDateTime, setPickUpDateTime] = useState<Date | null>(null)
  const [dropOffDateTime, setDropOffDateTime] = useState<Date | null>(null)
  const [selectedTrailer, setSelectedTrailer] = useState<ITrailerFull | null>(null)

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [visibleRegion, seVisibleRegion] = useState<TVisibleRegion | null>(null)
  const [isOpenCreateBookingModal, setIsOpenCreateBookingModal] = useState<boolean>(false)

  useEffect(() => {
    dispatch(Actions_TrailerTypes.saga.loadPage())

    return () => {
      dispatch(Actions_Bookings.store.setSelectedPickUpParking(null))
      dispatch(Actions_Bookings.store.setSelectedTrailerTypeId(null))
    }
  }, [])

  useEffect(() => {
    if (visibleRegion) {
      dispatch(
        Actions_Bookings.saga.getPickUpParkings({
          ...visibleRegion,
          fromDate: pickUpDateTime || null,
          toDate: dropOffDateTime || null,
          types: selectedTrailerTypeId,
        }),
      )
    }
  }, [visibleRegion, pickUpDateTime, dropOffDateTime, selectedTrailerTypeId])

  // const filterPassedTime: TFilterTime = (time) => {
  //   const currentDate = new Date()
  //   const selectedDate = new Date(time)
  //   return currentDate.getTime() < selectedDate.getTime()
  // }

  useEffect(() => {
    if (map && pickUpLocation) {
      const newCenter = new google.maps.LatLng(+pickUpLocation.lat, +pickUpLocation.lon)
      map.panTo(newCenter)
    }
  }, [map, pickUpLocation])

  const onMapLoad = useCallback(function callback(map: google.maps.Map) {
    setMap(map)
  }, [])

  const onUnmount = useCallback(function callback(map: google.maps.Map) {}, [])

  const onIdle = useCallback(() => {
    if (map) {
      const bounds = map.getBounds()

      if (!bounds) {
        return
      }
      const southWest = bounds.getSouthWest()
      const northEast = bounds.getNorthEast()

      seVisibleRegion({
        southWesternLatitude: southWest.lat(),
        southWesternLongitude: southWest.lng(),
        northEasternLatitude: northEast.lat(),
        northEasternLongitude: northEast.lng(),
      })
    }
  }, [map])

  const trailerTypesOptions = trailerTypes
    ? trailerTypes.map(({ type: { id, name } }) => ({ id, value: id, label: name }))
    : []

  const onClickTrailer = (trailer: ITrailerFull) => {
    setSelectedTrailer(trailer)
    setIsOpenCreateBookingModal(true)
  }

  return (
    <Shared.Page className={classes.root}>
      <Header />

      <Grid container spacing={1} mb={'1rem'} mt={1}>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <LocationInput location={pickUpLocation} onChangeLocation={setPickUpLocation} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <DatePicker
            value={pickUpDateTime}
            onChange={setPickUpDateTime}
            onClear={() => setPickUpDateTime(null)}
            placeholderText="Pick-up date and time"
            //filterTime={filterPassedTime}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          <DatePicker
            value={dropOffDateTime}
            onChange={setDropOffDateTime}
            onClear={() => setDropOffDateTime(null)}
            placeholderText="Drop-off date and time"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
          {/* <LocationInput /> */}
          <Dropdown
            value={selectedTrailerTypeId || undefined}
            name="trailerType"
            onChange={(value) => dispatch(Actions_Bookings.store.setSelectedTrailerTypeId(value))}
            options={trailerTypesOptions}
            marginTop={0}
            fullWidth
            placeholder="Trailer type"
          />
        </Grid>
      </Grid>
      <div className={classes.mapWrapper}>
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            zoom={googleMapsZoom}
            center={googleMapsDefaultCenter}
            onLoad={onMapLoad}
            onUnmount={onUnmount}
            options={googleMapsComponentOptions}
            onIdle={onIdle}
            onClick={() => dispatch(Actions_Bookings.store.setSelectedPickUpParking(null))}
          >
            <Markers />
          </GoogleMap>
        )}

        {selectedPickUpParking && <TrailerList onClickTrailer={onClickTrailer} />}

        {!isLoaded && <Shared.Preloader mask />}
        <CreateBookingModal
          isOpen={isOpenCreateBookingModal}
          closeModal={() => setIsOpenCreateBookingModal(false)}
          trailer={selectedTrailer}
          pickUpDateTime={pickUpDateTime}
          dropOffDateTime={dropOffDateTime}
        />
      </div>
    </Shared.Page>
  )
}

export default CreateBooking

const useStyles = makeStyles()((theme) => ({
  root: {
    alignItems: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    padding: '1.5rem',
    paddingTop: 0,
    backgroundColor: theme.palette.background.default,
    height: '100%',
  },
  mapWrapper: {
    //flexGrow: 2,
    position: 'relative',
    flex: 1,
    height: '500px',
    width: '100%',
    minHeight: 0,
  },
}))
